<template>
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <a href="/" aria-label="Go to home page"
            ><i class="fa fa-home"></i>Home
          </a>
        </li>
        <li class="breadcrumb-item active" aria-current="page">
          {{ folderName }}
        </li>
      </ol>
    </nav>
    <div class="card">
      <div class="card-header">
        <div class="card-title text-capitalize">Roster</div>
        <div
          v-if="isCurrentMonth"
          class="month-toggle-txt"
          @click="
            () => {
              toggleMonthHandler(false);
            }
          "
        >
          <i class="fa fa-calendar-check-o" aria-hidden="true"></i> Next Month
        </div>
        <div
          v-else
          class="month-toggle-txt"
          @click="
            () => {
              toggleMonthHandler(true);
            }
          "
        >
          <i class="fa fa-calendar-check-o" aria-hidden="true"></i> Current
          Month
        </div>
      </div>
      <div class="card-body">
        <div class="time-selector">
          <div v-if="impersonationShowComputed" class="impersonation-email">
            <div class="input-group">
              <input
                type="text"
                v-model="email"
                class="form-control"
                placeholder="crew's email"
                @keyup="toLower"
              />
              <div class="input-group-append">
                <span class="input-group-text">{{ emailDomain }}</span>
              </div>
            </div>
          </div>
          <div class="day-select-box" :class="{ error: validError }">
            <div class="label-txt">Start Date</div>
            <date-time-input
              :input-type="'date'"
              :set-date="rangeStart"
              :range-date="false"
              :editable="false"
              :clearable="false"
              :min-date="minDate"
              :max-date="maxDate"
              @changeDate="
                (obj) => {
                  changeDate('rangeStart', obj);
                }
              " />
          </div>
          <div class="day-select-box" :class="{ error: validError }">
            <div class="label-txt">End Date</div>
            <date-time-input
              :input-type="'date'"
              :set-date="rangeEnd"
              :range-date="false"
              :editable="false"
              :clearable="false"
              :min-date="minDate"
              :max-date="maxDate"
              @changeDate="
                (obj) => {
                  changeDate('rangeEnd', obj);
                }
              "
            />
          </div>
          <p class="error-txt" v-if="validError">
            The Start Date should be earlier than the End Date
          </p>
          <button
            type="button"
            class="btn btn-secondary"
            aria-label="search"
            :disabled='isLoading'
            @click="searchRoster"
          >
            Search
          </button>
          <div>
              <p class="warn-txt text-center mb-md-4">
                This is the beta version of the roster view. 
                Please continue to rely on the information in           
                <a
                :href="'https://jxcrew.starlux-airlines.com/jxcrew/login'"
                target="_blank"
                class="text-sm mr-2"
                >JX Crew</a>for duty operations in case of any discrepancies.
              </p>
          </div>
          <div>
            <p class="update-time-txt mb-md-4">
              Roster Updated at: {{ currentTime }}
            </p>
          </div>
        </div>
        <div v-if="roster.length == 0">No Roster</div>
        <div v-for="(day, dayIdx) in roster" :key="dayIdx" class="mb-5" v-if="day.actvType != 99">
          <p class="duty-list-title">{{ day.startDate }}</p>
          <div class="duty-list-item">
            <div
              class="d-flex align-items-center justify-content-between px-4 py-3"
              @click="toggleCollapse(dayIdx)"
            >
              <h4 v-if="day.actvType > 4 && day.actvType !== 9 " class="mb-0 font-weight-bolder">
                {{ day.subActv[0].subActvName }} 
              </h4>
              <h4 v-else class="mb-0 font-weight-bolder">
                {{ day.subActv[0].subActvName }} / {{ day.subActv[0].toAirp }}
                <span class="text-grey"
                  >RPT: {{ day.subActv[0].subSignOnTime }}
                  {{ day.subActv[0].subSignOnTz }}</span>
              </h4>
              <i
                class="duty-list-icon fa fa-angle-left text-grey"
                :style="{
                  transform:
                    isCollapsed == dayIdx ? 'rotate(-90deg)' : 'rotate(0deg)',
                }"
              ></i>
            </div>
            <div v-if="isCollapsed == dayIdx" class="p-4">
              <h4 v-if="day.remarks" class="mb-5 text-grey">Remarks: {{ day.remarks }}</h4>
              <div v-for="task in day.subActv" class="mb-4" v-if="task.subActvName != 'LO'">
                <p class="font-weight-bolder mb-1">{{ task.subActvName }}</p>
                <div v-if="day.actvType < 5 || day.actvType == 9">
                  <h4 v-if="day.actvType == 0 || day.actvType == 3 || day.actvType == 9" 
                  class="text-grey">Position: {{ task.position }}</h4>
                  <div v-if="day.actvType == 0">
                    <h4 class="text-grey">Aircraft: {{ task.aircReg }}</h4>
                    <h4 v-if="task.subSignOnDate != null" class="text-grey">
                      RPT: {{ task.subSignOnDate.split('.')[0] }} {{ task.subSignOnTime }} 
                      {{ task.subSignOnTz }}
                    </h4>
                    <table>
                      <tr>
                        <td class="table-left">{{ task.fromAirp }} </td>
                        <td></td>
                        <td class="table-right">{{ task.toAirp }}</td>
                      </tr>
                      <tr class="table-date text-grey">
                        <td class="table-left">{{ task.subStartDate.split('.')[0] }} </td>
                        <td class="table-middle">
                          <i class="fa fa-plane rotate mx-2"></i>
                        </td>
                        <td class="table-right">{{ task.subEndDate.split('.')[0] }}</td>
                      </tr>
                      <tr class="table-time text-grey">
                        <td class="table-left">{{ task.subStartTime }} </td>
                        <td></td>
                        <td class="table-right">{{ task.subEndTime }}</td>
                      </tr>
                    </table>
                  </div>
                  <div v-else>
                    <h4 v-if="task.subSignOnDate != null" class="text-grey">
                      RPT: {{ task.subSignOnDate.split('.')[0] }} {{ task.subSignOnTime }} 
                      {{ task.subSignOnTz }}
                    </h4>
                    <table>
                      <tr>
                        <td class="table-left">{{ task.fromAirp }} </td>
                        <td></td>
                        <td class="table-right"></td>
                      </tr>
                      <tr class="table-date text-grey">
                        <td class="table-left">{{ task.subStartDate.split('.')[0] }} </td>
                        <td class="table-middle">
                          <i class="non-flight-actv"> - </i>
                        </td>
                        <td class="table-right">{{ task.subEndDate.split('.')[0] }}</td>
                      </tr>
                      <tr class="table-time text-grey">
                        <td class="table-left">{{ task.subStartTime }} </td>
                        <td></td>
                        <td class="table-right">{{ task.subEndTime }}</td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import { mockData } from '../../mockData/QA20230605';
import { mapActions } from 'vuex';
import DateTimeInput from '@/components/DateTimeInput.vue';
import dayjs from 'dayjs';

const timeFormat = 'YYYY-MM-DD';

export default {
  name: 'ThirtyDaySchedule',
  components: {
    DateTimeInput,
  },
  data: () => ({
    emailDomain: '@starlux-airlines.com',
    folderName: '',
    isCollapsed: null,
    isCurrentMonth: true,
    roster: [],
    email: '', //unknown
    rangeStart: dayjs().format(timeFormat),
    rangeEnd: dayjs().add(7, 'day').format(timeFormat)
      ? dayjs().endOf('month').format(timeFormat)
      : dayjs().add(7, 'day').format(timeFormat),
    validError: false,
    isLoading: false,
    currentTime : dayjs().format('YYYY-MM-DD HH:mm:ss')

  }),
  computed: {
    impersonationShowComputed() {
      return this.$store.getters.isImpersonation;
    },
    minDate() {
      return this.isCurrentMonth
        ? dayjs().startOf('month').subtract(1, 'day').format('YYYY-MM-DD')
        : dayjs()
            .add(1, 'month')
            .startOf('month')
            .subtract(1, 'day')
            .format('YYYY-MM-DD');
    },
    maxDate() {
      return this.isCurrentMonth
        ? dayjs().endOf('month').format('YYYY-MM-DD')
        : dayjs().add(1, 'month').endOf('month').format('YYYY-MM-DD');
    },
  },
  methods: {
    ...mapActions(['getCrewRosterInfo']),
    toggleMonthHandler(bool) {
      this.isCurrentMonth = bool;
      if (bool) {
        this.rangeStart = dayjs().format(timeFormat);
        this.rangeEnd = dayjs().add(7, 'day').format(timeFormat);
      } else {
        this.rangeStart = dayjs()
          .add(1, 'month')
          .startOf('month')
          .format(timeFormat);
        this.rangeEnd = dayjs()
          .add(1, 'month')
          .startOf('month')
          .add(7, 'day')
          .format(timeFormat);
      }
    },
    toggleCollapse(idx) {
      this.isCollapsed = this.isCollapsed === idx ? null : idx;
    },
    changeDate(key, obj) {
      // console.log(obj);
      let val = obj.value ? dayjs(obj.value).format('YYYY-MM-DD') : null;
      switch (key) {
        case 'rangeEnd': {
          this.rangeEnd = val;
          break;
        }
        case 'rangeStart': {
          this.rangeStart = val;
          break;
        }
      }
    },
    async searchRoster() {
      if (
        dayjs(this.rangeStart).isAfter(this.rangeEnd) ||
        !this.rangeStart ||
        !this.rangeEnd
      ) {
        this.validError = true;
        this.roster = [];
        return false;
      }
      this.validError = false;
      try {
        this.isLoading = true;
        const params = {
          rosterStart: dayjs(this.rangeStart).format('YYYY-MM-DD HH:mm:ss'),
          rosterEnd: dayjs(this.rangeEnd).format('YYYY-MM-DD 23:59:59'),
          emplEmail: this.email
            ? this.email + this.emailDomain
            : localStorage.getItem('mail'),
        };
        const res = await this.getCrewRosterInfo(params);
        this.roster = res;
        this.currentTime = dayjs().format('YYYY-MM-DD HH:mm:ss');
      } catch (error) {
        // console.log(error);
        this.roster = [];
      }
      this.isLoading = false;
    },
    toLower(val) {
      this.email = val.target.value.toLowerCase();
    },
  },
  async created() {
    this.folderName = this.$route.name;
  },
  async mounted() {
    await this.searchRoster();
  },
};
</script>

<style lang="scss" scoped>
.duty-list-title {
  font-weight: 500;
  font-size: 17px;
  padding: 5px 15px;
  background: #f4f4f4;
  border-radius: 8px;
}

.duty-list-item {
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 8px;
  margin-bottom: 10px;
}

.duty-list-icon {
  transition: transform 0.3s ease;
  font-size: 1.5em;
}

.rotate {
  transform: rotate(45deg);
}

.time-selector {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 15px 0;
}

.day-select-box {
  display: flex;
  .mx-datepicker {
    flex-grow: 2;
  }

  &.error {
    .mx-datepicker {
      outline: 1px solid #ff5353;
    }
  }
}

.label-txt {
  padding: 6px 0;
  width: 100px;
}

.month-toggle-txt {
  transition: transform 0.3s ease;
  font-size: 14px;
  display: inline-block;
  position: relative;
  padding-bottom: 2px;
  cursor: pointer;
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 2px;
    background-color: #b99075;
  }
}

.input-group-text {
  font-size: 14px;
}

.error-txt {
  color: #ff5353;
  font-size: 14px;
  margin-bottom: 0;
}

.warn-txt {
  font-size: 12px;
}
.table-date .update-time-txt .table-time {
  font-size: 12px;
}
.table-left {
  text-align: left
}
.table-middle {
  text-align: center
}
.table-right {
  text-align: right
}
.fa-plane:before .non-flight-actv {
  margin: 0 10px;
}
</style>
